/**
 * Experiment results: a reviewable, side-by-side gallery of lab outputs (e.g. camera-LoRA variants on the same photos).
 *
 * Data lives in the media bucket the dashboard API already signs for the CRM:
 *   <bucket>/experiments/index.json                – ExperimentIndex
 *   <bucket>/experiments/<slug>/manifest.json      – ExperimentManifest
 *   <bucket>/experiments/<slug>/<photo>/<arm>.jpg  – full-resolution images
 *   <bucket>/experiments/<slug>/<item>/<arm>.mp4   – playable films (media_type "video")
 * Entries are published by the lab tooling (see docs); the CRM only reads.
 *
 * A "video" experiment keeps the same shape: `photos` are the items (e.g. Echoes), `videos` holds
 * one playable film per item × arm, and `images` holds the poster frames used for thumbnails.
 */

export type MediaRef = {
  bucket: string;
  key: string;
  width?: number;
  height?: number;
};

export type ExperimentMediaType = "image" | "video";

/** A playable film; `duration` is in seconds. */
export type VideoRef = MediaRef & {
  duration?: number;
  content_type?: string;
  /** An audio sample published as a title-card film; the gallery plays it as audio. */
  audio_only?: boolean;
};

/**
 * `compare` (default): pick one item and view its arms side by side.
 * `gallery`: every item on one page as a card with its own players, for entries judged by ear.
 */
export type ExperimentLayout = "compare" | "gallery";

export type ExperimentSummary = {
  slug: string;
  title: string;
  date: string;
  author?: string;
  summary?: string;
  tags?: string[];
  cover?: MediaRef;
  manifest: MediaRef;
  media_type: ExperimentMediaType;
  photo_count?: number;
  arm_count?: number;
};

export type ExperimentIndex = {
  version: 1;
  experiments: ExperimentSummary[];
};

export type ExperimentArm = {
  id: string;
  label: string;
  description?: string;
  /** "source" arms are the untouched inputs; "control" arms are baselines; everything else is a candidate. */
  kind?: "source" | "control" | "candidate";
};

export type ExperimentPhoto = {
  id: string;
  label?: string;
  caption?: string;
};

export type ExperimentMetricsColumn = {
  key: string;
  label: string;
  help?: string;
};

export type ExperimentMetricsRow = {
  arm: string;
  values: Record<string, string | number | null>;
};

export type ExperimentManifest = {
  version: 1;
  slug: string;
  title: string;
  date: string;
  author?: string;
  summary?: string;
  description_md?: string;
  tags?: string[];
  media_type: ExperimentMediaType;
  layout: ExperimentLayout;
  arms: ExperimentArm[];
  photos: ExperimentPhoto[];
  /** keyed by `${photoId}/${armId}`; posters (thumbnails) for a video experiment */
  images: Record<string, MediaRef>;
  /** keyed by `${photoId}/${armId}`; empty for an image experiment */
  videos: Record<string, VideoRef>;
  metrics?: {
    columns: ExperimentMetricsColumn[];
    rows: ExperimentMetricsRow[];
  };
  links?: { label: string; url: string }[];
  /** Optional: lets administrators generate new images with this entry's recipe on the GPU towers. */
  playground?: ExperimentPlayground;
};

export type PlaygroundLora = {
  id: string;
  label: string;
  /** null = render without a LoRA (strength has no effect). */
  file: string | null;
  description?: string;
};

export type PlaygroundPrompt = {
  id: string;
  label: string;
  /** Shown to the operator; the dashboard API fills {trigger}/{gear} and sends the text to the worker. */
  template: string;
  description?: string;
};

/**
 * The `playground` block of a Lab results manifest: everything an operator may vary when generating
 * new images with this entry's recipe. The dashboard API re-reads and enforces it; the CRM only renders it.
 */
export type ExperimentPlayground = {
  title?: string;
  description?: string;
  loras: PlaygroundLora[];
  prompts: PlaygroundPrompt[];
  allowCustomPrompt: boolean;
  strength: { min: number; max: number; step: number; default: number };
  /** Strengths rendered by "Strength sweep" (one job each, same seed). */
  sweepStrengths: number[];
  defaults: { lora?: string; prompt?: string };
  /** Seed per photo id used by the published arms, so a playground render can reproduce them. */
  seeds: Record<string, number>;
};

export const imageKey = (photoId: string, armId: string) =>
  `${photoId}/${armId}`;
